import { Component, OnInit, Input, Output, EventEmitter, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ToastService } from 'angular-toastify';

import { ApiTopupService } from '../../../../../../core/services/api/cityparking/api-topup.service';
import { TopupMapper } from '../../../../../../core/mappers/cityparking/topup.mapper';
import {
  TopupAccountDetail,
  TopupTransaction,
  TopupHold
} from '../../../../../../core/models/cityparking/topup.model';
import { PssLoadingComponent } from '../../../../../../tools/template/pss/pss-loading/pss-loading.component';
import { PssConfirmModalComponent } from '../../../../../../tools/template/pss/pss-confirm-modal/pss-confirm-modal.component';
import { ModalAdminActivityHistoryComponent } from '../../../system-settings/modal-admin-activity-history/modal-admin-activity-history.component';
import { AdminActivityEntityTypes } from '../../../../../../core/models/system-settings/admin-activity-log.model';

@Component({
  selector: 'app-modal-topup-detail',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    PssLoadingComponent,
    PssConfirmModalComponent,
    ModalAdminActivityHistoryComponent,
  ],
  templateUrl: './modal-topup-detail.component.html',
  styleUrl: './modal-topup-detail.component.css'
})
export class ModalTopupDetailComponent implements OnInit {
  private readonly apiService = inject(ApiTopupService);
  private readonly mapper = inject(TopupMapper);
  private readonly toast = inject(ToastService);

  // ================= Input & Output Properties =================
  @Input() accountId: number = 0;
  @Input() userId: number = 0;
  @Input() menuId: number = 0;
  @Output() close = new EventEmitter<void>();
  @Output() saveSuccess = new EventEmitter<void>();

  readonly activityEntityTypes = AdminActivityEntityTypes;
  showActivityHistory = false;

  // ================= State Variables =================
  isLoading: boolean = false;
  isVisible: boolean = false;

  // --- Account Detail States ---
  accountDetail: TopupAccountDetail | null = null;
  detailTab: 'recent' | 'all' | 'holds' = 'recent';

  // --- Detail Tab Paginations ---
  accTxCurrentPage: number = 1;
  accTxPageSize: number = 20;
  accTxTotalItems: number = 0;
  accTxTotalPages: number = 0;
  accTransactions: TopupTransaction[] = [];
  isAccTxPageSizeDropdownOpen: boolean = false;

  accHoldCurrentPage: number = 1;
  accHoldPageSize: number = 20;
  accHoldTotalItems: number = 0;
  accHoldTotalPages: number = 0;
  accHolds: TopupHold[] = [];
  isAccHoldPageSizeDropdownOpen: boolean = false;

  pageSizeOptions: number[] = [20, 50, 100];

  // --- Adjust Balance Dialog Form States ---
  isAdjustOpen: boolean = false;
  adjustMethod: number = 3; // 3 = Increase, 4 = Decrease
  adjustAmount: number | null = null;
  adjustRemark: string = '';

  // --- Confirm Modal States ---
  isConfirmOpen: boolean = false;

  // ================= Normal Functions =================

  /**
   * Initialize component, trigger transition animation, and load account details
   */
  ngOnInit(): void {
    setTimeout(() => {
      this.isVisible = true;
    }, 50);
    if (this.accountId) {
      this.loadAccountDetail(this.accountId);
    }
  }

  /**
   * Close modal with fade-out transition animation
   */
  closeModal(): void {
    this.isVisible = false;
    setTimeout(() => {
      this.close.emit();
    }, 200);
  }

  /**
   * Toggle between recent transactions, all transactions, and hold sections
   * @param tab Selected tab name
   */
  switchDetailTab(tab: 'recent' | 'all' | 'holds'): void {
    this.detailTab = tab;
    if (tab === 'all') {
      this.loadAccountTransactions();
    } else if (tab === 'holds') {
      this.loadAccountHolds();
    }
  }

  /**
   * Open the balance adjustments modal popup
   */
  openAdjustBalanceDialog(): void {
    if (!this.accountDetail || !this.accountDetail.canAdjustBalance) return;
    this.adjustMethod = 3;
    this.adjustAmount = null;
    this.adjustRemark = '';
    this.isAdjustOpen = true;
  }

  /**
   * Close the balance adjustments modal popup
   */
  closeAdjustBalanceDialog(): void {
    this.isAdjustOpen = false;
  }

  /**
   * Validate balance adjustment entries and open confirmation modal
   */
  submitAdjustRequest(): void {
    if (!this.adjustAmount || this.adjustAmount <= 0) {
      this.toast.error('จำนวนเงินต้องมากกว่า 0');
      return;
    }
    if (!this.adjustRemark.trim()) {
      this.toast.error('กรุณาระบุหมายเหตุ');
      return;
    }
    this.isConfirmOpen = true;
  }

  /**
   * Cancel balance adjustment confirmation modal popup
   */
  cancelConfirm(): void {
    this.isConfirmOpen = false;
  }

  /**
   * Toggle page size selection dropdown list for transactions tab
   */
  toggleAccTxPageSizeDropdown(): void {
    this.isAccTxPageSizeDropdownOpen = !this.isAccTxPageSizeDropdownOpen;
  }

  /**
   * Safely dismiss page size selection dropdown for transactions tab
   */
  closeAccTxPageSizeDropdown(): void {
    setTimeout(() => {
      this.isAccTxPageSizeDropdownOpen = false;
    }, 200);
  }

  /**
   * Select pagination page size limit for transactions tab list
   * @param size Number of items to display
   */
  selectAccTxPageSize(size: number): void {
    this.accTxPageSize = size;
    this.accTxCurrentPage = 1;
    this.isAccTxPageSizeDropdownOpen = false;
    this.loadAccountTransactions();
  }

  /**
   * Navigate to target page of transactions history tab list
   * @param page Target page index
   */
  setAccTxPage(page: number): void {
    if (page >= 1 && page <= this.accTxTotalPages) {
      this.accTxCurrentPage = page;
      this.loadAccountTransactions();
    }
  }

  /**
   * Starting index number of current page items display range for transactions tab
   */
  get accTxStartItemIndex(): number {
    if (this.accTxTotalItems === 0) return 0;
    return (this.accTxCurrentPage - 1) * this.accTxPageSize + 1;
  }

  /**
   * Ending index number of current page items display range for transactions tab
   */
  get accTxEndItemIndex(): number {
    return Math.min(this.accTxCurrentPage * this.accTxPageSize, this.accTxTotalItems);
  }

  /**
   * Numeric page index options array list to render in transactions list footer
   */
  get accTxPagesArray(): number[] {
    const total = this.accTxTotalPages;
    const maxVisible = 5;
    let start = Math.max(1, this.accTxCurrentPage - 2);
    let end = Math.min(total, start + maxVisible - 1);
    if (end - start < maxVisible - 1) {
      start = Math.max(1, end - maxVisible + 1);
    }
    return Array.from({ length: end - start + 1 }, (_, i) => start + i);
  }

  /**
   * Toggle page size selection dropdown list for holds tab
   */
  toggleAccHoldPageSizeDropdown(): void {
    this.isAccHoldPageSizeDropdownOpen = !this.isAccHoldPageSizeDropdownOpen;
  }

  /**
   * Safely dismiss page size selection dropdown list for holds tab
   */
  closeAccHoldPageSizeDropdown(): void {
    setTimeout(() => {
      this.isAccHoldPageSizeDropdownOpen = false;
    }, 200);
  }

  /**
   * Select pagination page size limit for holds tab list
   * @param size Number of items to display
   */
  selectAccHoldPageSize(size: number): void {
    this.accHoldPageSize = size;
    this.accHoldCurrentPage = 1;
    this.isAccHoldPageSizeDropdownOpen = false;
    this.loadAccountHolds();
  }

  /**
   * Navigate to target page of holds record tab list
   * @param page Target page index
   */
  setAccHoldPage(page: number): void {
    if (page >= 1 && page <= this.accHoldTotalPages) {
      this.accHoldCurrentPage = page;
      this.loadAccountHolds();
    }
  }

  /**
   * Starting index number of current page items display range for holds tab
   */
  get accHoldStartItemIndex(): number {
    if (this.accHoldTotalItems === 0) return 0;
    return (this.accHoldCurrentPage - 1) * this.accHoldPageSize + 1;
  }

  /**
   * Ending index number of current page items display range for holds tab
   */
  get accHoldEndItemIndex(): number {
    return Math.min(this.accHoldCurrentPage * this.accHoldPageSize, this.accHoldTotalItems);
  }

  /**
   * Numeric page index options array list to render in holds list footer
   */
  get accHoldPagesArray(): number[] {
    const total = this.accHoldTotalPages;
    const maxVisible = 5;
    let start = Math.max(1, this.accHoldCurrentPage - 2);
    let end = Math.min(total, start + maxVisible - 1);
    if (end - start < maxVisible - 1) {
      start = Math.max(1, end - maxVisible + 1);
    }
    return Array.from({ length: end - start + 1 }, (_, i) => start + i);
  }

  // ================= API Functions =================

  /**
   * Fetch account profile information details from service API by ID
   * @param id Account record target ID
   */
  loadAccountDetail(id: number): void {
    this.isLoading = true;
    this.apiService.getTopupAccountById(id, this.userId, this.menuId).subscribe({
      next: (res) => {
        console.log("modal-detail getTopupAccountById", res);
        this.isLoading = false;
        this.accountDetail = this.mapper.mapAccountDetail(res);
      },
      error: (err) => {
        this.isLoading = false;
        this.toast.error('ไม่สามารถโหลดรายละเอียดบัญชีได้');
        console.error(err);
        this.closeModal();
      }
    });
  }

  /**
   * Fetch transactions history records linked to account ID from service API
   */
  loadAccountTransactions(): void {
    if (!this.accountId) return;
    this.isLoading = true;
    this.apiService.getTopupTransactionsByAccountId(this.accountId, this.accTxCurrentPage, this.accTxPageSize).subscribe({
      next: (res) => {
        console.log("modal-detail getTopupTransactionsByAccountId", res);
        this.isLoading = false;
        const mapped = this.mapper.mapTransactionTable(res);
        this.accTransactions = mapped.data;
        this.accTxTotalItems = mapped.total;
        this.accTxTotalPages = Math.ceil(this.accTxTotalItems / this.accTxPageSize) || 1;
      },
      error: (err) => {
        this.isLoading = false;
        console.error(err);
      }
    });
  }

  /**
   * Fetch active reservation holds linked to account ID from service API
   */
  loadAccountHolds(): void {
    if (!this.accountId) return;
    this.isLoading = true;
    this.apiService.getTopupHoldsByAccountId(this.accountId, this.accHoldCurrentPage, this.accHoldPageSize).subscribe({
      next: (res) => {
        console.log("modal-detail getTopupHoldsByAccountId", res);
        this.isLoading = false;
        const mapped = this.mapper.mapHoldTable(res);
        this.accHolds = mapped.data;
        this.accHoldTotalItems = mapped.total;
        this.accHoldTotalPages = Math.ceil(this.accHoldTotalItems / this.accHoldPageSize) || 1;
      },
      error: (err) => {
        this.isLoading = false;
        console.error(err);
      }
    });
  }

  /**
   * Execute balance adjustment call via command API endpoint and refresh view details
   */
  confirmAdjustBalance(): void {
    this.isConfirmOpen = false;
    this.isAdjustOpen = false;

    if (!this.accountDetail || !this.adjustAmount) return;

    this.isLoading = true;

    const payload = {
      userId: this.userId,
      menuId: this.menuId,
      topupAccountId: this.accountDetail.id,
      topupMethod: this.adjustMethod,
      amount: this.adjustAmount,
      remark: this.adjustRemark
    };

    this.apiService.requestAdjustTopupAccountBalance(payload).subscribe({
      next: (res) => {
        this.isLoading = false;
        if (res.isSuccess) {
          this.toast.success(res.message || 'บันทึกรายการสำเร็จ');
          // Reload Account Detail & History Tab
          this.loadAccountDetail(this.accountId);
          if (this.detailTab === 'all') {
            this.loadAccountTransactions();
          }
          this.saveSuccess.emit();
        } else {
          this.toast.error(res.message || 'ปรับปรุงยอดล้มเหลว');
        }
      },
      error: (err) => {
        this.isLoading = false;
        this.toast.error(err?.error?.message || 'ปรับปรุงยอดล้มเหลว');
      }
    });
  }

  get canShowActivityHistory(): boolean {
    return this.accountId > 0;
  }

  openActivityHistory(): void {
    if (!this.canShowActivityHistory) return;
    this.showActivityHistory = true;
  }

  closeActivityHistory(): void {
    this.showActivityHistory = false;
  }
}
